<template>
  <div class="text-white font-bold p-0" :class="[!showHeaderBg ? 'bg-pdsPrimaryColor' : 'bg-primary-default']">
    <div class="ml-auto w-11/12">
      <div class="grid grid-rows md:grid-cols md:grid-cols-3">
        <!-- Patient Details Section -->
        <div v-if="showPatientDetails() && patientGegevens" class="py-4">
          <!-- Back Link -->
          <div class="text-sm mb-1 cursor-pointer -ml-1" v-if="!$isInIframe">
            <router-link to="/">
              <ChevronLeftIcon class="h-5 w-5 inline-block align-text-top" aria-hidden="true" />
              <span class="align-middle">Patiënt zoeken</span>
            </router-link>
          </div>

          <!-- Patient Name and Status -->
          <div
            v-if="computedPatientName"
            class="mb-1 flex items-center space-x-1"
            :class="{ 'pointer-events-none cursor-not-allowed': patientGegevens.IsArchived }"
          >
            <img
              v-if="patientGegevens.IsDeceased"
              :src="require('@/static/img/christianity.svg')"
              class="inline-block"
            />
            <router-link to="/patientdashboard" class="text-lg md:text-xl lg:text-xl">
              {{ computedPatientName }}
            </router-link>

            <!-- Edit Icon -->
            <PencilIcon
              v-if="!patientGegevens.IsArchived && !$isInIframe"
              class="h-4 w-4 cursor-pointer"
              aria-hidden="true"
              v-tooltip="{ content: 'Gegevens wijzigen', placement: 'bottom' }"
              @click="editPatientDetails"
            />
          </div>

          <!-- Additional Patient Info -->
          <div class="text-sm font-medium">
            <span>{{ moment(patientGegevens.GeboorteDatum).format('DD-MM-YYYY') }}</span>
            <span v-if="patientGegevens.BSNnummer || patientGegevens.BSN">
              | {{ patientGegevens.BSNnummer || patientGegevens.BSN }}
            </span>
            <span class="capitalize"> | {{ patientInsurer }} </span>
            <span v-if="(isOrderConfirmation() || isAssortment()) && activeDepartment" class="ml-1">
              Aanvullen magazijn {{ activeDepartment.name }}
            </span>
          </div>
        </div>
        <div class="justify-items-end place-content-center space-x-2 mr-12 col-span-2">
          <!-- Action buttons -->
          <div
            class="flex justify-end place-content-end items-start md:items-end self-start flex-wrap flex-row mb-4 lg:mb-0 gap-y-2"
            v-if="!$isInIframe"
          >
            <button
              @click.prevent="$router.push('/catalog')"
              v-if="
                showPatientDetails() &&
                licenseOverviews &&
                (licenseOverviews.hasActiveLicenses || licenseOverviews.otherDeptLicense) &&
                !patientGegevens.IsDeceased &&
                !isUserDeviceManager &&
                showStatus
              "
              class="flex justify-center py-2 px-2 md:px-4 shadow-sm text-sm font-medium text-white bg-cta rounded-full hover:bg-primary-default/50 items-center mr-2"
            >
              <PlusIcon class="h-3 md:h-5 lg:h-5 w-3 md:w-5 lg:w-5 inline-block mr-1" aria-hidden="true" />
              <span class="">Nieuwe bestelling</span>
            </button>
            <patient-transfer-button
              v-if="showPatientTransferButton && showPatientDetails() && !isUserDeviceManager && formPurpose !== 'new' && showStatus"
              :change-user-for-patient-transfer="changeUserForPatientTransferStatus"
              :show-transfer-button="getShowTransferButtonStatus()"
              @close="closePatientTransfer()"
            />
            <patient-follow :is-following="isFollowed" v-if="showPatientDetails() && showStatus" />
            <!-- Cart Section -->
            <v-cart v-if="showCart() && !$isInIframe" class="mr-2" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { ChevronLeftIcon, PencilIcon, PlusIcon } from '@heroicons/vue/20/solid';
import { useStore, mapActions } from 'vuex';
import { computed, ref, onUnmounted, onMounted, onBeforeUnmount, onBeforeMount, getCurrentInstance } from 'vue';
import router from '@/router';
import moment from 'moment';
import VCart from '@/components/sections/catalog/cart/Index';
import { activeDepartment$ } from '@/services';
import PatientTransferButton from '@/components/sections/patientTransfer/PatientTransferButton';
import PatientFollow from '@/components/sections/patient/PatientFollow';
import { currentUserIdentity$, licenseOverviews$, customerSecurityPolicySettings$ } from '@/services';

export default {
  name: 'Patientheader',
  components: {
    ChevronLeftIcon,
    PencilIcon,
    VCart,
    PatientTransferButton,
    PatientFollow,
    PlusIcon
  },
  setup() {
    const $emitter = getCurrentInstance().appContext.config.globalProperties.$emitter;
    const store = useStore();
    const user = ref(null);
    const activeDepartment = ref(null);
    const customerSecurityPolicySettings = ref(null);
    const licenseSubscription = ref(null);
    const licenseOverviews = ref(null);
    const changeUserForPatientTransferStatus = ref(false);
    const formPurpose = computed(() => store.getters['patient/getFormPurpose']);
    const showPatientTransferButton = ref(false); // E40 - 9761 - Disable patient transfer function.

    currentUserIdentity$.subscribe((identity) => {
      user.value = identity;
    });
    if (formPurpose.value !== 'new') {
      customerSecurityPolicySettings$.subscribe((settings) => {
        customerSecurityPolicySettings.value = settings;
      });
      licenseSubscription.value = licenseOverviews$.subscribe((overviews) => {
        licenseOverviews.value = overviews;
      });
    }

    const computedPatientName = computed(() => {
      const { GeslachtId, Voorletters, Voornaam, Tussenvoegsel, Achternaam, IsArchived } = patientGegevens.value;

      return [
        aanhefFormat(GeslachtId),
        Voorletters || Voornaam,
        Tussenvoegsel,
        Achternaam,
        IsArchived ? '- Inactief' : null
      ]
        .filter(Boolean)
        .join(' ');
    });
    const getCurrentPatient = () => store.dispatch('patient/getCurrentPatient');
    const isFollowed = computed(() => store.getters['patient/getPatientFollowStatus']);
    const patientGegevens = computed(() => store.getters['patient/getPatientGegevens']);
    const patientFormData = computed(() => store.getters['patient/getAllPatientFormData']);
    const patientInsurer = computed(() => store.getters['patient/getPatientInsurer']);
    const resetPatientGegevens = () => store.commit('patient/setPatientGegevens', {});
    const resetPatientInsurer = () => store.commit('patient/setPatientInsurer', '');
    const isUserDeviceManager = computed(() => {
      return user.value ? false : user.value.isDeviceManager;
    });
    const transferredTo = computed(() => {
      return (customerSecurityPolicySettings.value && customerSecurityPolicySettings.value.transferredTo) || null;
    });
    const transferredFrom = computed(() => {
      return (customerSecurityPolicySettings.value && customerSecurityPolicySettings.value.transferredFrom) || [];
    });
    activeDepartment$.subscribe((value) => {
      activeDepartment.value = value;
    });
    const showStatus = computed(() => {
      return router.currentRoute.value.name === 'patientdashboard';
    });

    const aanhefFormat = (value) => {
      if (value === 1) {
        return 'Dhr. ';
      } else if (value === 2) {
        return 'Mevr. ';
      } else {
        return '';
      }
    };

    const getShowTransferButtonStatus = () => {
      return !transferredTo.value && transferredFrom.value.length === 0;
    };
    const closePatientTransfer = () => {
      changeUserForPatientTransferStatus.value = false;
    };

    onMounted(() => {
      $emitter.on('showPatientTransfer', (props) => {
        changeUserForPatientTransferStatus.value = props;
      });
    });
    onBeforeUnmount(() => {
      if (licenseSubscription.value) {
        licenseSubscription.value.unsubscribe();
      }
    });
    onUnmounted(() => {
      if (!router.currentRoute.value.meta.patientheader && router.currentRoute.value.href !== '/service') {
        resetPatientGegevens();
      }
      resetPatientInsurer();
    });

    onBeforeMount(async () => {
      if (formPurpose.value !== 'new') {
        await getCurrentPatient();
      }
    });

    return {
      getCurrentPatient,
      computedPatientName,
      patientGegevens,
      patientFormData,
      formPurpose,
      patientInsurer,
      ...mapActions('search', ['resetSearch']),
      ...mapActions('patient', ['setEndpoints']),
      activeDepartment,
      resetPatientGegevens,
      licenseOverviews,
      isUserDeviceManager,
      changeUserForPatientTransferStatus,
      getShowTransferButtonStatus,
      transferredTo,
      transferredFrom,
      closePatientTransfer,
      user,
      isFollowed,
      aanhefFormat,
      showStatus,
      showPatientTransferButton
    };
  },
  data() {
    return {
      patientEditButton: false,
      patientName: true,
      showHeaderBg: true
    };
  },
  created() {},
  methods: {
    // Function to show patient details or not based on route name
    showPatientDetails() {
      // Always show patient details when in iframe
      if (this.$isInIframe) {
        this.showHeaderBg = true;
        return true;
      }

      let currentPath = JSON.stringify(router.currentRoute.value.path);
      if (currentPath.includes('/aanvullenmagazijn')) {
        this.showHeaderBg = false;
        return false;
      }
      this.showHeaderBg = true;
      return router.currentRoute.value.meta.hidePatientDetails !== true;
    },
    // Function to check if we should show gegevens wijzigen
    checkGegevensWijzigen() {
      return router.currentRoute.value.name === 'patientgegevens';
    },
    // Function to reset search and redirect to home page
    searchAgain(e) {
      this.resetSearch();
      router.push('/');
    },
    // Function to edit patient details
    editPatientDetails(e) {
      e.preventDefault();
      this.setEndpoints('edit');
      var url = '/patientgegevens';
      router.push(url);
      return false;
    },
    // Function to convert datetime to required specific format
    moment(...args) {
      return moment(...args);
    },
    // Function to whether show the cart or not
    showCart() {
      return router.currentRoute.value.meta.showShoppingCart === true;
    },
    isAssortment() {
      return router.currentRoute.value.flow === 'replenishment';
    },
    isOrderConfirmation() {
      return router.currentRoute.value.name === 'orderconfirmation';
    }
  }
};
</script>
